import React, { useEffect, useState } from "react"
import { IQuery, IQueryBuilderContext, IStepValidationResponse, StepType, IDataSource, IHistoryStep, IAggregationType, IHistoryBucket } from "@types"
import { findSourceById, getHistoryEnabledFields, getSelectedStepDetailsById,  handleStepFieldValueChanges, hasHistoryEnabledForField,  registerStep, validateFieldConfiguration, validateSource, validateStep, validateStepNameAndId } from "@utils"
import { FormSection } from "@components/FormSection/FormSection"
import { SourceSelectorInput } from "../SourceSelector/SourceSelectorInput"
import { hasValue, Select } from "uxp/components"
import { ExtendedDatePicker, parseRelativeDate, serialiseRelativeDate } from "../ExtendedDatePicker/ExtendedDatePicker"
import Icon from '@images/trends.svg'

export function renderStep(context: IQueryBuilderContext, query: IQuery, onChange: (query: IQuery) => void, selectedStepId: string) {
    const { step, index, previousSteps, selectedSources } = getSelectedStepDetailsById(query, selectedStepId)
    const _step: IHistoryStep = Object.assign({}, step as IHistoryStep)

    const [errors, setErrors] = useState<Record<string, string>>({})

    useEffect(() => {
        const { isValid, errors } = validateStep(query, step as IHistoryStep)
        setErrors(errors)
    }, [step])

    const source = findSourceById(query, _step?.source)
    const historicalSource = findSourceById(query, _step?.historicalDataSourceId)

    async function onValueChange(field: string, value: any, source?: boolean) {
        const _query = await handleStepFieldValueChanges(context, query, selectedStepId, field, value, source)
        onChange(_query)
    }

    return <React.Fragment>
        <FormSection
            label='Source'
            error={errors?.source}
        >
            <SourceSelectorInput
                source={source}
                onChange={(newSource) => { onValueChange('source', newSource, true) }}
                availableSources={[...previousSteps, ...selectedSources]}
                query={query}
            />
        </FormSection>

        <FormSection
            label='Field'
            error={errors?.field}
        >
            <Select
                selected={_step.field || ''}
                onChange={(updated) => onValueChange('field', updated)}
                options={(source?.schema || []).map(f => ({ label: f.name, value: f.name }))}
            />
        </FormSection>

        <FormSection
            label='Historical data source '
            error={errors?.historicalDataSourceId}
        >
            <SourceSelectorInput
                source={historicalSource}
                onChange={(newSource) => { onValueChange('historicalDataSourceId', newSource, true) }}
                availableSources={[...selectedSources]}
                history={true}
                query={query}
            />
        </FormSection>

        <FormSection
            label='Historical source field'
            error={errors?.historicalDataSourceField}
        >
            <Select
                selected={_step.historicalDataSourceField || ''}
                onChange={(updated) => onValueChange('historicalDataSourceField', updated)}
                options={((getHistoryEnabledFields(historicalSource as IDataSource) || [])).map(f => ({ label: f.name, value: f.name }))}
            />
        </FormSection>

        <FormSection
            label='Aggregation'
            error={errors?.aggregation}
        >
            <Select
                selected={_step.aggregation || ''}
                onChange={(updated) => onValueChange('aggregation', updated)}
                options={(Object.entries(IAggregationType) || []).map(f => ({ label: f[0], value: f[1] }))}
            />
        </FormSection>

        <FormSection
            label='Bucket'
            error={errors?.bucket}
        >
            <Select
                selected={_step.bucket || ''}
                onChange={(updated) => onValueChange('bucket', updated)}
                options={(Object.entries(IHistoryBucket) || []).map(f => ({ label: f[0], value: f[1] }))}
            />
        </FormSection>

        <FormSection
            label='Start Date'
            error={errors?.starts}
        >
            <ExtendedDatePicker
                datetime={_step.starts || '0d'}
                onChange={(updated) => onValueChange('starts', updated)}
            />
        </FormSection>

        <FormSection
            label='End Date'
            error={errors?.ends}
        >
            <ExtendedDatePicker
                datetime={_step.ends || '0d'}
                onChange={(updated) => onValueChange('ends', updated)}
            />
        </FormSection>


    </React.Fragment>
}

function validate(query: IQuery, step: IHistoryStep): IStepValidationResponse {
    const errors: Record<string, string> = validateStepNameAndId(step);
    // validate source
    const source = validateSource(query, step, 'source')
    if (!source.isValid) errors.source = source.error

    const historySource = validateSource(query, step, 'historicalDataSourceId', true, true)
    if (!historySource.isValid) errors.historicalDataSourceId = historySource.error

    const sourceField = validateFieldConfiguration(query, step, [step.field], 'source')
    if (!sourceField?.isValid) errors.field = sourceField.error

    const historyField = validateFieldConfiguration(query, step, [step.historicalDataSourceField], 'historicalDataSourceId')
    if (!historyField?.isValid) errors.historicalDataSourceField = historyField.error

    if (!hasHistoryEnabledForField((historySource?.source as IDataSource), step.historicalDataSourceField)) errors.historicalDataSourceField = 'Historical data is not enabled for this field'

    if (!hasValue(step?.aggregation)) errors.aggregation = 'Aggregation is required'
    if (!hasValue(step?.bucket)) errors.bucket = 'Bucket is required'
    if (!hasValue(step?.starts)) errors.starts = 'Start date is required'
    if (!hasValue(step?.ends)) errors.ends = 'End date is required'

    return { isValid: Object.keys(errors).length === 0, errors };
}

registerStep({
    type: StepType.History,
    name: "Historical Data",
    description: "Query the historical values of a given collection and field. ",
    icon: Icon,
    iconStyles: { backgroundSize: '55%'},
    render: renderStep,
    validate: validate,
    onSelectStep: (basicProps, previousSource) => {
        return { ...basicProps, source: previousSource, aggregation: 'average', bucket: 'hour', ends: '0d', starts: '-7d', field: '', historicalDataSourceField: '', historicalDataSourceId: '', } as IHistoryStep
    }
})